import React, { useEffect, useState } from 'react';
import { BsPlus } from 'react-icons/bs';
import CustomBarChart from '../analytics/CustomBarChart';
import { useNavigate } from 'react-router-dom';
import api from '../../services/Api';
import { formatShortDate } from '../../utils/Date';

const Subscription = () => {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [planDetails, setPlanDetails] = useState([]);
  const [annualData, setAnnualData] = useState([]);
  const [filteredSubscriptions, setFilteredSubscriptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await api.get('/analytics/subscriptions');

        const subscriptionsData = response.data?.subscriptions || [];
        setSubscriptions(subscriptionsData);

        const firstTenSubscriptions = subscriptionsData.slice(0, 10);
        setFilteredSubscriptions(firstTenSubscriptions);

        const years = extractYearsFromSubscriptions(subscriptionsData);
        setAvailableYears(years);

        if (years.length > 0) {
          setSelectedYear(years[0]);
        }
      } catch (error) {
        console.error('Error fetching subscriptions:', error);
        setError('Failed to load subscription data');
        setSubscriptions([]);
        setFilteredSubscriptions([]);
        setAvailableYears(extractYearsFromSubscriptions([]));
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const extractYearsFromSubscriptions = subs => {
    if (!subs || subs.length === 0) {
      const currentYear = new Date().getFullYear();
      return [currentYear - 2, currentYear - 1, currentYear];
    }

    try {
      const years = subs.map(sub => new Date(sub.startDate).getFullYear());
      const uniqueYears = [...new Set(years)].sort((a, b) => b - a);

      if (uniqueYears.length === 0) {
        const currentYear = new Date().getFullYear();
        return [currentYear - 2, currentYear - 1, currentYear];
      }

      return uniqueYears;
    } catch (error) {
      const currentYear = new Date().getFullYear();
      return [currentYear - 2, currentYear - 1, currentYear];
    }
  };

  useEffect(() => {
    const fetchPlanData = async () => {
      try {
        const response = await api.get('/admin/plans');
        const plansData = response.data?.plans || [];
        const firstThreePlans = plansData.slice(0, 3);
        setPlanDetails(firstThreePlans);
      } catch (error) {
        console.error('Error fetching plans:', error);
        setPlanDetails([]);
      }
    };

    fetchPlanData();
  }, []);

  useEffect(() => {
    if (subscriptions.length > 0) {
      const subsForYear = subscriptions.filter(sub => {
        try {
          const subYear = new Date(sub.startDate).getFullYear();
          return subYear === selectedYear;
        } catch (error) {
          return false;
        }
      });

      const monthlyData = processMonthlyData(subsForYear);
      setAnnualData(monthlyData);
    } else {
      setAnnualData(processMonthlyData([]));
    }
  }, [selectedYear, subscriptions]);

  const processMonthlyData = subs => {
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    const monthlyData = months.map(month => ({ month, value: 0 }));

    if (!subs || subs.length === 0) {
      return monthlyData;
    }

    subs.forEach(sub => {
      try {
        const month = new Date(sub.startDate).getMonth();
        if (month >= 0 && month < 12) {
          monthlyData[month].value += 1;
        }
      } catch (error) {
        console.warn('Invalid date in subscription data:', sub.startDate);
      }
    });

    return monthlyData;
  };

  const handleYearChange = e => {
    setSelectedYear(parseInt(e.target.value));
  };

  const handleViewSubscription = () => {
    navigate('/subscription');
  };

  const handleManageSubscription = () => {
    navigate('/subscription-plan');
  };

  const EmptyPlansState = () => (
    <div className="w-4/5 p-4 space-y-4">
      <div className="py-8 text-center">
        <div className="mb-2 text-gray-400">
          <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        </div>
        <p className="text-sm font-light text-gray-500">No subscription plans available</p>
      </div>
    </div>
  );

  const EmptyPaymentsState = () => (
    <div className="space-y-4">
      <div className="py-8 text-center">
        <div className="mb-2 text-gray-400">
          <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1}
              d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
            />
          </svg>
        </div>
        <p className="text-sm font-normal text-gray-500">No payments this month</p>
      </div>
    </div>
  );

  const ErrorState = ({ message }) => (
    <div className="py-4 text-center">
      <div className="mb-2 text-red-400">
        <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1}
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>
      <p className="text-xs font-light text-red-500">{message}</p>
    </div>
  );

  return (
    <div className="grid grid-cols-3 gap-8 font-nohemi">
      <div className="col-span-2 p-4">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-base text-[#111827] font-semibold mb-2">
              Get started with SubsManage
            </h2>
            <p className="text-sm text-[#111827]">
              View, progress and manage subscriptions efficiently
            </p>
          </div>
          <button
            className="bg-[#E3B616] text-white text-[11px] px-4 py-2 rounded-2xl flex items-center mr-8"
            onClick={handleManageSubscription}>
            <BsPlus className="w-4 h-4 mr-1" />
            Add Subscription
          </button>
        </div>

        <div className="mb-8">
          <h3 className="font-normal text-sm font-semibold text-[#111827] mb-4">My Subscriptions</h3>

          {error ? (
            <ErrorState message={error} />
          ) : planDetails.length === 0 ? (
            <EmptyPlansState />
          ) : (
            <>
              <div className="w-4/5 p-4 space-y-2">
                {planDetails.map((plan, index) => (
                  <div key={plan._id || index} className="flex items-center p-2 rounded-lg">
                    <div className="flex items-center w-2/3 space-x-2">
                      <div className="w-8 h-8 p-2 text-sm font-light text-center bg-gray-100 rounded">
                        {index + 1}
                      </div>
                      <div className="font-light text-sm text-[#111827] truncate">
                        {plan?.name || 'Unnamed Plan'}
                      </div>
                    </div>
                    <div className="font-light text-sm text-[#111827] w-1/3 px-2">
                      {plan?.planCode || 'N/A'}
                    </div>
                    <div className="font-light text-sm text-[#111827] w-1/3 text-right">
                      {plan?.interval || 'No interval'}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-start mx-6 mt-4">
                <button
                  onClick={handleManageSubscription}
                  className="w-1/3 py-3 text-sm text-white bg-black rounded-lg">
                  View all subscriptions
                </button>
              </div>
            </>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base text-[#111827] font-medium">
              Total subscriptions for {selectedYear}
            </h3>
            <div className="relative">
              <select
                value={selectedYear}
                onChange={handleYearChange}
                className="block px-4 py-1 pr-8 text-sm text-gray-700 bg-white border border-gray-200 rounded-lg appearance-none focus:outline-none focus:ring-1 focus:ring-gray-300"
                disabled={isLoading}>
                {availableYears.map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 text-gray-700 pointer-events-none">
                <svg
                  className="w-4 h-4 fill-current"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20">
                  <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                </svg>
              </div>
            </div>
          </div>
          <div className="relative">
            {isLoading ? (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white bg-opacity-70">
                <div className="w-8 h-8 border-t-2 border-b-2 border-gray-900 rounded-full animate-spin"></div>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center py-12 rounded bg-gray-50">
                <ErrorState message="Failed to load chart data" />
              </div>
            ) : (
              <CustomBarChart
                data={annualData}
                subtitle={`Monthly Subscription Count for ${selectedYear}`}
                userCount={
                  annualData.length > 0 ? Math.max(...annualData.map(item => item.value)) + 1 : 5
                }
              />
            )}
          </div>
        </div>
      </div>

      <div className="col-span-1 p-4">
        <h3 className="text-base text-[#111827] font-semibold mb-4 mt-6">Payments This Month</h3>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="w-6 h-6 border-t-2 border-b-2 border-gray-900 rounded-full animate-spin"></div>
          </div>
        ) : error ? (
          <ErrorState message="Failed to load payment data" />
        ) : filteredSubscriptions.length === 0 ? (
          <EmptyPaymentsState />
        ) : (
          <>
            <div className="space-y-4">
              {filteredSubscriptions.map((subs, index) => {
                try {
                  const startDate = new Date(subs.startDate);
                  const endDate = new Date(startDate);
                  endDate.setMonth(endDate.getMonth() + 1);

                  return (
                    <div key={subs._id || index} className="flex items-start space-x-4">
                      <div className="w-8 p-2 text-sm font-light text-center bg-gray-100 rounded-full">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="font-bold text-sm text-gray-900">
                          {subs.businessDetails?.name || 'Unknown Business'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {subs.planDetails?.name || 'No Plan Name'}
                        </div>
                      </div>
                      <div className="text-sm font-light text-gray-600">
                        {formatShortDate(subs.startDate)} - {formatShortDate(endDate.toISOString())}
                      </div>
                    </div>
                  );
                } catch (dateError) {
                  return (
                    <div key={index} className="flex items-start space-x-4">
                      <div className="w-8 p-2 text-sm font-light text-center bg-gray-100 rounded-full">
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="font-light text-[11px] text-[#111827]">
                          Invalid Date Data
                        </div>
                      </div>
                    </div>
                  );
                }
              })}
            </div>
            <button
              className="w-full py-3 mt-6 text-sm text-white bg-black rounded-lg"
              onClick={handleViewSubscription}>
              View Full List
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default Subscription;
